import React, { useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Briefcase, User, CheckCircle2, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { saveSubmission } from '../services/databaseService';
import { sendViaWeb3Forms } from '../services/web3formsService';
import { Link } from 'react-router-dom';
type FormType = 'employeur' | 'professionnel' | null;
// Brand colors
const NAVY = '#1E3A5F';
const GOLD = '#D4A574';
export function InscriptionPage() {
  const [activeForm, setActiveForm] = useState<FormType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consentAccepted) return;
    setIsSubmitting(true);
    const formData = new FormData(formRef.current!);
    const data: Record<string, any> = {};
    formData.forEach((value, key) => {
      if (data[key]) {
        if (Array.isArray(data[key])) {
          data[key].push(value);
        } else {
          data[key] = [data[key], value];
        }
      } else {
        data[key] = value;
      }
    });
    // Format checkboxes
    const interet = formData.getAll('interet').join(', ');
    const metiers = formData.getAll('metiers').join(', ');
    let submission: any = {};
    if (activeForm === 'employeur') {
      submission = {
        type: 'promaison_employeur',
        nomComplet: data.nomComplet || '',
        telephone: data.telephone || '',
        email: data.email || '',
        quartier: data.quartier || '',
        interet: interet || '',
        disponibilite: data.disponibilite || '',
        commentaire: data.commentaire || '',
        consentement: consentAccepted ? 'Oui' : 'Non'
      };
    } else if (activeForm === 'professionnel') {
      submission = {
        type: 'promaison_professionnel',
        nomComplet: data.nomComplet || '',
        telephone: data.telephone || '',
        age: data.age || '',
        quartier: data.quartier || '',
        metiers: metiers || '',
        experience: data.experience || '',
        disponibilite: data.disponibilite || '',
        consentement: consentAccepted ? 'Oui' : 'Non'
      };
    }
    try {
      saveSubmission(submission);
      // Prepare fields for Web3Forms
      const fields: Record<string, string> = {
        ...submission
      };
      delete fields.type;
      await sendViaWeb3Forms({
        type: submission.type,
        fields
      });
      setIsSubmitting(false);
      setIsSuccess(true);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error saving submission:', error);
      setIsSubmitting(false);
      // Show success anyway to not block user
      setIsSuccess(true);
      window.scrollTo(0, 0);
    }
  };
  const resetForm = () => {
    setActiveForm(null);
    setIsSuccess(false);
    setConsentAccepted(false);
  };
  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent outline-none transition-all bg-gray-50/50';
  const labelClass = 'text-sm font-semibold text-gray-700 mb-1.5 block';
  return <Layout data-id="element-1524" data-mp="1hobrvf1mkxue">
      <SEO title="Inscription PRO MAISON | ADCC" description="Inscrivez-vous au parcours PRO MAISON. Que vous soyez employeur ou professionnel(le) des services à domicile, rejoignez le programme." url="https://adcc.africa/inscription" data-id="element-1525" data-mp="1hov3ke0ompzz" />

      <div className="min-h-screen bg-gray-50 pb-20" data-id="element-1526" data-mp="13qdiyp62ptma">
        {/* Header Mobile-First */}
        <div className="bg-white border-b border-gray-100 pt-8 pb-6 px-4 text-center sticky top-0 z-10 shadow-sm" data-id="element-1527" data-mp="3shxx6155diq4">
          <div className="max-w-3xl mx-auto" data-id="element-1528" data-mp="3oers2wtrtxl8">
            <img src="https://cdn.magicpatterns.com/uploads/xq7xorXDBCdoHQja5rTP8f/PROMAISON_logo_extrait.png" alt="PRO MAISON" className="h-16 w-auto mx-auto mb-3 object-contain" data-id="element-1529" data-mp="3oi7aa8iqrmqv" />
            <p className="text-sm font-bold tracking-widest uppercase mb-2" style={{
            color: GOLD
          }} data-id="element-1530" data-mp="169gwvj8h65hn">
              La dignité par la compétence
            </p>
            <p className="text-gray-600 text-sm leading-relaxed max-w-lg mx-auto" data-id="element-1531" data-mp="1yy5qyyb45ajw">
              Rejoignez le parcours certifiant PRO MAISON. Les
              professionnel(le)s accompagné(e)s seront mis en avant sur la
              future plateforme DIGNWORK (lancement prévu en octobre 2026).
            </p>
          </div>
        </div>

        <div className="max-w-xl mx-auto px-4 pt-8" data-id="element-1532" data-mp="ehsof7sti343">
          {isSuccess ? <Card className="border-none shadow-xl text-center overflow-hidden" data-id="element-1533" data-mp="ejvqwrb5popp">
              <div className="h-2 w-full bg-green-500" data-id="element-1534" data-mp="ikxnf0dovmnz"></div>
              <CardContent className="p-8 md:p-10" data-id="element-1535" data-mp="az29zhhi3qlg">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1536" data-mp="b1y6r2hm498b">
                  <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-1537" data-mp="ky18i78jy5uq" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1538" data-mp="kvos3yig03js">
                  {activeForm === 'employeur' ? 'Merci !' : 'Merci pour votre inscription !'}
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-1539" data-mp="3jg6unx96wbu5">
                  {activeForm === 'employeur' ? "Un membre de l'équipe ADCC vous contactera prochainement." : "L'équipe ADCC vous recontactera pour la suite du parcours PRO MAISON."}
                </p>
                <Button onClick={resetForm} className="w-full py-4 text-base" style={{
              backgroundColor: NAVY
            }} data-id="element-1540" data-mp="35ngvss8kbcfm">
                  Retour à l'accueil
                </Button>
              </CardContent>
            </Card> : !activeForm ? <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]" data-id="element-1541" data-mp="3ucjtaebvy643">
              <h1 className="text-xl font-bold text-center text-gray-900 mb-6" data-id="element-1542" data-mp="30s79aze25am">
                Sélectionnez votre profil :
              </h1>

              {/* Carte Employeur */}
              <button onClick={() => setActiveForm('employeur')} className="w-full group text-left bg-white rounded-2xl p-6 shadow-sm hover:shadow-md border-2 border-transparent hover:border-[#1E3A5F] transition-all" data-id="element-1543" data-mp="37fi6qzzxreov">
                <div className="flex items-start gap-4" data-id="element-1544" data-mp="3ldyhd20x5pca">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors" style={{
                backgroundColor: '#EEF4FB',
                color: NAVY
              }} data-id="element-1545" data-mp="3lfjbwrn8h8a5">
                    <Briefcase className="w-6 h-6" data-id="element-1546" data-mp="2l59sa5psz9kz" />
                  </div>
                  <div data-id="element-1547" data-mp="2g89m7usye6tv">
                    <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1548" data-mp="2g91kjddxqinu">
                      Je suis employeur / gérant(e) de résidence
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed" data-id="element-1549" data-mp="26g3zazhk6flj">
                      Vous employez ou cherchez du personnel de maison qualifié.
                    </p>
                  </div>
                </div>
              </button>

              {/* Carte Professionnel */}
              <button onClick={() => setActiveForm('professionnel')} className="w-full group text-left bg-white rounded-2xl p-6 shadow-sm hover:shadow-md border-2 border-transparent hover:border-[#D4A574] transition-all" data-id="element-1550" data-mp="1sgkr66oqz498">
                <div className="flex items-start gap-4" data-id="element-1551" data-mp="3hwaor582ip2p">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors" style={{
                backgroundColor: '#FDF8F3',
                color: GOLD
              }} data-id="element-1552" data-mp="3hwtuzoocvp0w">
                    <User className="w-6 h-6" data-id="element-1553" data-mp="2hy4r44mamuaw" />
                  </div>
                  <div data-id="element-1554" data-mp="2lzgd805ykxec">
                    <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-1555" data-mp="2lyordh25ye19">
                      Je suis professionnel(le)
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed" data-id="element-1556" data-mp="2cza1aipws5ha">
                      Vous travaillez (ou voulez travailler) dans l'entretien,
                      la cuisine, la garde d'enfants...
                    </p>
                  </div>
                </div>
              </button>
            </div> : <div className="animate-[popIn_0.3s_ease-out]" data-id="element-1557" data-mp="3teuulfzc180t">
              <button onClick={() => setActiveForm(null)} className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-id="element-1558" data-mp="1a0l91v072n4">
                <ArrowLeft className="w-4 h-4" data-id="element-1559" data-mp="1hqzmlb3v9br6" />
                Changer de profil
              </button>

              <Card className="border-none shadow-xl overflow-hidden" data-id="element-1560" data-mp="1vi4gdwkjz9tl">
                <div className="h-2 w-full" style={{
              backgroundColor: activeForm === 'employeur' ? NAVY : GOLD
            }} data-id="element-1561" data-mp="1qr4ianzp6lpi"></div>
                <CardContent className="p-6 md:p-8" data-id="element-1562" data-mp="20hh3mifc6iwp">
                  <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100" data-id="element-1563" data-mp="20go7ebe07bst">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: activeForm === 'employeur' ? '#EEF4FB' : '#FDF8F3',
                  color: activeForm === 'employeur' ? NAVY : GOLD
                }} data-id="element-1564" data-mp="2efxkv3atrrt3">
                      {activeForm === 'employeur' ? <Briefcase className="w-5 h-5" data-id="element-1565" data-mp="1ytc4yxg6cxe1" /> : <User className="w-5 h-5" data-id="element-1566" data-mp="1urh6s1tjj04h" />}
                    </div>
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-1567" data-mp="1pse0ke1tqnux">
                      {activeForm === 'employeur' ? 'Formulaire Employeur' : 'Formulaire Professionnel(le)'}
                    </h2>
                  </div>

                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" data-id="element-1568" data-mp="2ihux9nwh8qwy">
                    {/* --- FORMULAIRE EMPLOYEUR --- */}
                    {activeForm === 'employeur' && <>
                        <div data-id="element-1569" data-mp="368qpun8rbq59">
                          <label className={labelClass} data-id="element-1570" data-mp="31g7abkqiekxt">Nom complet *</label>
                          <input type="text" name="nomComplet" required className={inputClass} placeholder="Votre nom et prénom" data-id="element-1571" data-mp="31e2vi7r2o1ir" />
                        </div>
                        <div data-id="element-1572" data-mp="3mrcwmas44myo">
                          <label className={labelClass} data-id="element-1573" data-mp="3mrmpq78gfa8l">
                            Téléphone (WhatsApp) *
                          </label>
                          <input type="tel" name="telephone" required className={inputClass} placeholder="+229..." data-id="element-1574" data-mp="3ro3m1opt4uwr" />
                        </div>
                        <div data-id="element-1575" data-mp="eb1kn1iy11ar">
                          <label className={labelClass} data-id="element-1576" data-mp="ec2zlh32y7xr">Email</label>
                          <input type="email" name="email" className={inputClass} placeholder="votre@email.com (optionnel)" data-id="element-1577" data-mp="j9v2t796dgh9" />
                        </div>
                        <div data-id="element-1578" data-mp="2p32k4e840ule">
                          <label className={labelClass} data-id="element-1579" data-mp="2p3c8jxdiatoz">
                            Quartier / Résidence *
                          </label>
                          <input type="text" name="quartier" required className={inputClass} placeholder="Ex: Haie Vive" data-id="element-1580" data-mp="2ka9e7n0wng7q" />
                        </div>
                        <div data-id="element-1581" data-mp="37zkgj8xwlinz">
                          <label className={labelClass} data-id="element-1582" data-mp="332lqb1hd1vv2">
                            Je suis intéressé(e) par : *
                          </label>
                          <div className="space-y-3 mt-2" data-id="element-1583" data-mp="1dq02dfu9xyzn">
                            {['Faire former mon personnel actuel', 'Recruter du personnel certifié', 'Les deux'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-1584" data-mp="3pd3juob02npv">
                                <input type="checkbox" name="interet" value={option} className="w-5 h-5 text-[#1E3A5F] border-gray-300 rounded focus:ring-[#1E3A5F]" data-id="element-1585" data-mp="31k3m73p34jfp" />
                                <span className="text-sm text-gray-700" data-id="element-1586" data-mp="qtn8c3hnzciv">
                                  {option}
                                </span>
                              </label>)}
                          </div>
                        </div>
                        <div data-id="element-1587" data-mp="62hxbcgq8qu3">
                          <label className={labelClass} data-id="element-1588" data-mp="15rbe8i8r10r">Disponibilité *</label>
                          <select name="disponibilite" required className={inputClass} data-id="element-1589" data-mp="13n3lfh81hv7">
                            <option value="" data-id="element-1590" data-mp="3nf2bg859dp7u">Sélectionnez...</option>
                            <option value="Maintenant" data-id="element-1591" data-mp="3jcnslo1lapgk">Maintenant</option>
                            <option value="À la rentrée (septembre)" data-id="element-1592" data-mp="3ejcqu1bmwyvi">
                              À la rentrée (septembre)
                            </option>
                            <option value="Pas encore décidé" data-id="element-1593" data-mp="34m8685xftbgt">
                              Pas encore décidé
                            </option>
                          </select>
                        </div>
                        <div data-id="element-1594" data-mp="2vlryuldkpk6o">
                          <label className={labelClass} data-id="element-1595" data-mp="2vmtdt0xpmqto">
                            Commentaire libre
                          </label>
                          <textarea name="commentaire" rows={3} className={inputClass} placeholder="Précisions sur vos besoins (optionnel)..." data-id="element-1596" data-mp="1b5kb5sc02fh3"></textarea>
                        </div>
                      </>}

                    {/* --- FORMULAIRE PROFESSIONNEL --- */}
                    {activeForm === 'professionnel' && <>
                        <div data-id="element-1597" data-mp="11doaqamfyd9p">
                          <label className={labelClass} data-id="element-1598" data-mp="11deei5asz9vk">Nom complet *</label>
                          <input type="text" name="nomComplet" required className={inputClass} placeholder="Votre nom et prénom" data-id="element-1599" data-mp="1644nrtmcnwvj" />
                        </div>
                        <div data-id="element-1600" data-mp="5mcii9s4c13t">
                          <label className={labelClass} data-id="element-1601" data-mp="5lkyzxbspttw">
                            Téléphone (WhatsApp) *
                          </label>
                          <input type="tel" name="telephone" required className={inputClass} placeholder="+229..." data-id="element-1602" data-mp="fcxpakhy4bxt" />
                        </div>
                        <div data-id="element-1603" data-mp="3et43mbbq4dkr">
                          <label className={labelClass} data-id="element-1604" data-mp="3etx10loslqq8">
                            Date de naissance ou âge
                          </label>
                          <input type="text" name="age" className={inputClass} placeholder="Ex: 25 ans ou 12/05/1998 (optionnel)" data-id="element-1605" data-mp="3nt0mtxfcgk8f" />
                        </div>
                        <div data-id="element-1606" data-mp="2l0emmieq3g5b">
                          <label className={labelClass} data-id="element-1607" data-mp="2l052vck9u582">
                            Quartier de résidence *
                          </label>
                          <input type="text" name="quartier" required className={inputClass} placeholder="Ex: Akpakpa" data-id="element-1608" data-mp="2pxeqqns0i8ts" />
                        </div>
                        <div data-id="element-1609" data-mp="38pzi20s2c5vh">
                          <label className={labelClass} data-id="element-1610" data-mp="38q8wcr3qwolc">
                            Métier(s) qui m'intéresse(nt) : *
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2" data-id="element-1611" data-mp="2uwhxqdrjyp4e">
                            {['Entretien du domicile', 'Cuisine', "Garde d'enfants", 'Jardinage', 'Sécurité / gardiennage', 'Autre'].map(metier => <label key={metier} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors" data-id="element-1612" data-mp="1nkqr7s996pze">
                                <input type="checkbox" name="metiers" value={metier} className="w-5 h-5 text-[#D4A574] border-gray-300 rounded focus:ring-[#D4A574]" data-id="element-1613" data-mp="2bacmw5qf2n2n" />
                                <span className="text-sm text-gray-700" data-id="element-1614" data-mp="1x9s5ne05gzb9">
                                  {metier}
                                </span>
                              </label>)}
                          </div>
                        </div>
                        <div data-id="element-1615" data-mp="2xjt4zmz4oqf7">
                          <label className={labelClass} data-id="element-1616" data-mp="2xkl0yywn0q60">Expérience *</label>
                          <select name="experience" required className={inputClass} data-id="element-1617" data-mp="31jtrzw0am9xv">
                            <option value="" data-id="element-1618" data-mp="3baoft4t6thlf">Sélectionnez...</option>
                            <option value="Aucune expérience" data-id="element-1619" data-mp="3g9hwf63rtopt">
                              Aucune expérience
                            </option>
                            <option value="Moins de 2 ans" data-id="element-1620" data-mp="3l79l7r94cups">
                              Moins de 2 ans
                            </option>
                            <option value="2 à 5 ans" data-id="element-1621" data-mp="3p8czi9biqiow">2 à 5 ans</option>
                            <option value="Plus de 5 ans" data-id="element-1622" data-mp="3u8hkocndg1lg">Plus de 5 ans</option>
                          </select>
                        </div>
                        <div data-id="element-1623" data-mp="1zuzs2b840ejk">
                          <label className={labelClass} data-id="element-1624" data-mp="1zwb0wpgewchj">
                            Disponibilité pour le parcours *
                          </label>
                          <select name="disponibilite" required className={inputClass} data-id="element-1625" data-mp="1onm42n3xkxeo">
                            <option value="" data-id="element-1626" data-mp="1ydfevlw918hv">Sélectionnez...</option>
                            <option value="Disponible maintenant" data-id="element-1627" data-mp="1yeqi9kls8e8g">
                              Disponible maintenant
                            </option>
                            <option value="Disponible à la rentrée" data-id="element-1628" data-mp="27dkkbqhvtwte">
                              Disponible à la rentrée
                            </option>
                          </select>
                        </div>
                      </>}

                    {/* Consentement (Commun) */}
                    <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 mt-8" data-id="element-1629" data-mp="i07chrak9qnj">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-1630" data-mp="mza8km8kcmjd">
                        <input type="checkbox" required checked={consentAccepted} onChange={e => setConsentAccepted(e.target.checked)} className="mt-1 w-5 h-5 text-[#1E3A5F] border-gray-300 rounded focus:ring-[#1E3A5F]" data-id="element-1631" data-mp="3091w0z16qtcj" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-1632" data-mp="3j7e6qp7qwba8">
                          J'accepte que mes données soient traitées par l'ADCC
                          dans le cadre du programme PRO MAISON, conformément à
                          la loi béninoise n°2017-20 sur la protection des
                          données à caractère personnel. *
                        </span>
                      </label>
                    </div>

                    <Button type="submit" className="w-full py-4 text-base shadow-lg" disabled={isSubmitting || !consentAccepted} style={{
                  backgroundColor: activeForm === 'employeur' ? NAVY : GOLD
                }} data-id="element-1633" data-mp="1yt44cbkbgdcv">
                      {isSubmitting ? <span className="flex items-center justify-center gap-2" data-id="element-1634" data-mp="1mb9uchsnbsyu">
                          <Loader2 className="w-5 h-5 animate-spin" data-id="element-1635" data-mp="1r6763whb8gff" />
                          Envoi en cours...
                        </span> : activeForm === 'employeur' ? 'Envoyer ma demande' : "M'inscrire au programme"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>}

          {/* Footer Contact Info */}
          <div className="mt-12 text-center text-sm text-gray-500 pb-8" data-id="element-1636" data-mp="tmm6qfkfcri4">
            <p className="mb-1" data-id="element-1637" data-mp="yil06hkm0zpe">Besoin d'aide ? Contactez-nous :</p>
            <p className="font-medium text-gray-700" data-id="element-1638" data-mp="ewrln8gr5ukr">
              <a href="mailto:contact@adcc.africa" className="hover:underline" data-id="element-1639" data-mp="eze07s229ach">
                contact@adcc.africa
              </a>{' '}
              |{' '}
              <a href="tel:+22901901175555" className="hover:underline" data-id="element-1640" data-mp="owid11a0ntgm">
                +229 01 90 11 75 55
              </a>
            </p>
          </div>
        </div>
      </div>
    </Layout>;
}